import {
    Alert,
    Box,
    Button,
    FormControl,
    FormControlLabel,
    Radio,
    RadioGroup,
    TextField,
    Typography,
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { z } from 'zod'
import {
    generateOtpForMFA,
    loginAndAuthenticate,
    logoutUser,
    setMfaConfig,
    updatePassword,
    verifyOtpForMFA,
} from '../../api/auth.js'
import { clearSessionAuth } from '../../utils/session'
// Zod Validation Schemas
const passwordSchema = z
  .object({
    old: z
      .string()
      .min(1, 'Old password is required')
      .min(16, 'Password must be at least 16 characters'),
    new: z
      .string()
      .min(16, 'New password must be at least 16 characters')
      .max(40, 'New password must not exceed 40 characters')
      .refine(value => {
        let count = 0
        if (/[a-z]/.test(value)) count++
        if (/[A-Z]/.test(value)) count++
        if (/\d/.test(value)) count++
        if (/[^A-Za-z\d]/.test(value)) count++
        return count >= 3
      }, 'Password must contain at least three of the following: uppercase letters, lowercase letters, numbers, or symbols'),
    confirm: z.string().min(1, 'Please confirm your password'),
  })
  .refine(data => data.new === data.confirm, {
    message: "Passwords don't match",
    path: ['confirm'],
  })
  .refine(data => data.old !== data.new, {
    message: 'New password must be different from old password',
    path: ['new'],
  })

const otpSchema = z.object({
  otp: z
    .string()
    .min(1, 'OTP is required')
    .length(6, 'OTP must be 6 digits')
    .regex(/^\d+$/, 'OTP must contain only numbers'),
})

const authenticatorSchema = z.object({
  authenticatorCode: z
    .string()
    .min(1, 'Authenticator code is required')
    .length(6, 'Authenticator code must be 6 digits')
    .regex(/^\d+$/, 'Code must contain only numbers'),
})

const steps = [
  'Change Password',
  'Enable Multi Factor Authentication',
  // 'Download App',
  // 'Scan QR Code',
]

export default function ResetPasswordForm({ userDetails, onClose }) {
  const isActiveStep =
    !userDetails?.mfaEnable && !userDetails?.newPassword ? 1 : 0
  const navigate = useNavigate()
  const [activeStep, setActiveStep] = useState(isActiveStep)
  const [passwords, setPasswords] = useState({
    old: '',
    new: '',
    confirm: '',
  })
  const [mfaMethod, setMfaMethod] = useState('mail')
  const [otp, setOtp] = useState('')
  const [authenticatorCode, setAuthenticatorCode] = useState('')
  const [otpSent, setOtpSent] = useState(false)
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState({})
  const [successMessage, setSuccessMessage] = useState('')
  const [otpSentDetails, setOtpSentDetails] = useState([])
  const [cooldown, setCooldown] = useState(0)

  useEffect(() => {
    let timer
    if (cooldown > 0) {
      timer = setInterval(() => {
        setCooldown(prev => prev - 1)
      }, 1000)
    }
    return () => clearInterval(timer)
  }, [cooldown])

  const handleClick = () => {
    generateOtp()
    setCooldown(30)
  }

  // Validation helper function
  const validateStep = step => {
    setErrors({})
    setSuccessMessage('')

    try {
      switch (step) {
        case 0:
          passwordSchema.parse(passwords)
          return true
        case 1:
          if (mfaMethod === 'mail' && otpSent) {
            otpSchema.parse({ otp })
          }
          return true
        case 3:
          if (mfaMethod === 'google') {
            authenticatorSchema.parse({ authenticatorCode })
          }
          return true
        default:
          return true
      }
    } catch (error) {
      if (error instanceof z.ZodError) {
        const fieldErrors = {}
        error.issues.forEach(err => {
          const path = err.path[0]
          fieldErrors[path] = err.message
        })
        setErrors(fieldErrors)
        return false
      }
      return false
    }
  }

  const handleNext = async () => {
    // Validate current step
    if (!validateStep(activeStep)) {
      return
    }

    if (activeStep === 0 && userDetails?.newPassword) {
      setLoading(true)
      try {
        const resp = await updatePassword({
          oldPassword: passwords.old,
          password: passwords.new,
          username: userDetails.preferred_username,
        })
        await loginAndAuthenticate({
          username: userDetails.preferred_username,
          password: passwords.new,
        })
        setSuccessMessage('Password updated successfully!')

        // Set OTP sent flag if using email MFA
        // if (mfaMethod === 'mail' && !otpSent) {
        //   setOtpSent(true);
        // }

        // Only proceed to next step on successful API call
        setTimeout(() => {
          setActiveStep(prevActiveStep => prevActiveStep + 1)
          setSuccessMessage('')
        }, 1500)
      } catch (error) {
        setErrors({
          api: error?.message || 'Failed to update password. Please try again.',
        })
      } finally {
        setLoading(false)
      }
    } else if (activeStep === 1 && mfaMethod === 'mail' && otpSent) {
      // Handle OTP verification
      setLoading(true)
      try {
        // Add your OTP verification API call here
        const payload = {
          secretLink: otpSentDetails.secretLink,
          username: userDetails.preferred_username,
          mfa: true,
          otp,
        }
        const configPaylod = {
          secretLink: otpSentDetails.secretLink,
          otpMFA: true,
          otp,
          isMFASet: false,
        }
        await verifyOtpForMFA(payload)
        await setMfaConfig(configPaylod)
        await logoutUser()
        clearSessionAuth()

        setSuccessMessage('OTP verified successfully!')
        navigate('/login')
        onClose()
        // setTimeout(() => {
        //   setActiveStep(prevActiveStep => prevActiveStep + 1)
        //   setSuccessMessage('')
        // }, 1500)
      } catch (error) {
        setErrors({
          otp: error.message || 'Invalid OTP. Please try again.',
        })
      } finally {
        setLoading(false)
      }
    } else if (activeStep === 3 && mfaMethod === 'google') {
      // Handle Google Authenticator verification
      setLoading(true)
      try {
        // Add your authenticator verification API call here
        // const resp = await verifyAuthenticator({ code: authenticatorCode, username: userDetails.preferred_username })
        setSuccessMessage('MFA setup completed successfully!')
        setTimeout(() => {
          window.location.replace(`${window.location.pathname}#/login`)
        }, 2000)
      } catch (error) {
        setErrors({
          authenticatorCode:
            error?.response?.data?.message ||
            'Invalid authenticator code. Please try again.',
        })
      } finally {
        setLoading(false)
      }
    } else {
      // For other steps, proceed directly
      setActiveStep(prevActiveStep => prevActiveStep + 1)
    }
  }

  const handleBack = () => {
    // Prevent going back from Step 1 if MFA not enabled
    if (activeStep === 1 && !userDetails?.mfaEnable) return

    setActiveStep(prevActiveStep => prevActiveStep - 1)
    setOtpSent(false)
    setErrors({})
    setSuccessMessage('')
  }

  const handlePasswordChange = field => event => {
    setPasswords(prev => ({
      ...prev,
      [field]: event.target.value,
    }))

    // Clear specific field error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }))
    }
  }

  const generateOtp = async () => {
    setLoading(true)
    try {
      // Add your generate OTP API call here
      const resp = await generateOtpForMFA({
        mfa: true,
        username: userDetails.preferred_username,
      })
      setOtpSent(true)
      setOtpSentDetails(resp)
    } catch (error) {
      setErrors({
        otp:
          error?.response?.data?.message ||
          'Failed to send OTP. Please try again.',
      })
    } finally {
      setLoading(false)
    }
  }

  const renderStepContent = step => {
    switch (step) {
      case 0:
        return (
          <Box
            sx={{
              mt: 3,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <Typography
              variant="h5"
              sx={{
                textAlign: 'center',
                fontWeight: 700,
                fontSize: '14px',
              }}
            >
              Change Password
            </Typography>

            {/* Success Message */}
            {successMessage && (
              <Alert severity="success" sx={{ width: '70%', mb: 2 }}>
                {successMessage}
              </Alert>
            )}

            {/* API Error */}
            {errors.api && (
              <Alert severity="error" sx={{ width: '70%', mb: 2 }}>
                {errors.api}
              </Alert>
            )}

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
                width: '70%',
              }}
            >
              <TextField
                fullWidth
                type="password"
                placeholder="Old Password"
                value={passwords.old}
                onChange={handlePasswordChange('old')}
                variant="outlined"
                error={!!errors.old}
                helperText={errors.old}
                sx={{
                  '.MuiOutlinedInput-input': {
                    padding: '12px 20px',
                  },
                }}
              />
              <TextField
                fullWidth
                type="password"
                placeholder="New Password"
                value={passwords.new}
                onChange={handlePasswordChange('new')}
                variant="outlined"
                error={!!errors.new}
                helperText={errors.new}
                sx={{
                  '.MuiOutlinedInput-input': {
                    padding: '12px 20px',
                  },
                }}
              />
              <TextField
                fullWidth
                type="password"
                placeholder="Confirm Password"
                value={passwords.confirm}
                onChange={handlePasswordChange('confirm')}
                variant="outlined"
                error={!!errors.confirm}
                helperText={errors.confirm}
                sx={{
                  '.MuiOutlinedInput-input': {
                    padding: '12px 20px',
                  },
                }}
              />
            </Box>
          </Box>
        )

      case 1:
        return (
          <Box sx={{ mt: 3 }}>
            <Typography
              variant="h5"
              sx={{
                textAlign: 'center',
                fontWeight: 700,
                fontSize: '14px',
                mb: 4,
              }}
            >
              Multi Factor Authentication
            </Typography>

            {/* Success Message */}
            {successMessage && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {successMessage}
              </Alert>
            )}

            <FormControl
              component="fieldset"
              sx={{
                width: '100%',
                '.MuiFormGroup-root': {
                  display: 'flex',
                  textAlign: 'center',
                  justifyContent: 'center',
                  flexDirection: 'row',
                  gap: 2,
                },
              }}
            >
              <RadioGroup
                value={mfaMethod}
                onChange={e => {
                  setMfaMethod(e.target.value)
                  setOtpSent(false)
                  setErrors({})
                }}
                sx={{ mb: 3 }}
              >
                <FormControlLabel
                  value="mail"
                  control={
                    <Radio
                      sx={{
                        color: 'var(--forbrand-color)',
                        '&.Mui-checked': { color: 'var(--forbrand-color)' },
                      }}
                    />
                  }
                  sx={{
                    '.MuiTypography-root': {
                      fontSize: '14px',
                    },
                  }}
                  label="Enable MFA using Mail OTP"
                />
                {/* <FormControlLabel
                  value="google"
                  label="Enable MFA using Google Authenticator"
                  sx={{
                    '.MuiTypography-root': {
                      fontSize: '14px',
                    },
                  }}
                  control={
                    <Radio
                      sx={{
                        color: 'var(--forbrand-color)',
                        '&.Mui-checked': { color: 'var(--forbrand-color)' },
                      }}
                    />
                  }
                /> */}
              </RadioGroup>

              {mfaMethod === 'mail' && (
                <Box
                  sx={{
                    background: '#e0e0e0',
                    display: 'flex',
                    justifyContent: 'center',
                    py: 3,
                    alignItems: 'center',
                    mx: 10,
                    px: 2,
                    borderRadius: '4px',
                  }}
                >
                  {!otpSent ? (
                    <Button
                      variant="contained"
                      onClick={handleClick}
                      disabled={loading}
                      sx={{
                        backgroundColor: 'var(--forbrand-bg-variant)',
                        color: 'var(--forbrand-brand-varient)',
                      }}
                    >
                      {loading ? 'Generating...' : 'Generate Otp'}
                    </Button>
                  ) : (
                    <>
                      <Button
                        variant="outlined"
                        sx={{
                          mb: 2,
                          color: 'var(--forbrand-primary)',
                          borderColor: '#ccc',
                          mr: 4,
                        }}
                        className="cursor-pointer-not-allowed"
                        onClick={handleClick}
                        disabled={cooldown > 0}
                      >
                        {cooldown > 0
                          ? `Generate OTP (${cooldown})`
                          : 'Generate OTP'}
                      </Button>

                      <Box className="flex flex-col gap-4 items-center">
                        <TextField
                          fullWidth
                          placeholder="Please Enter Mail OTP *"
                          value={otp}
                          type="number"
                          onChange={e => {
                            setOtp(e.target.value)
                            if (errors.otp) {
                              setErrors(prev => ({ ...prev, otp: '' }))
                            }
                          }}
                          variant="outlined"
                          error={!!errors.otp}
                          helperText={errors.otp}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: '#fff',
                              fontSize: '14px',
                              borderRadius: 'unset',
                              '& fieldset': {
                                borderColor: errors?.otp
                                  ? 'var(--forbrand-error)'
                                  : 'var(--forbrand-primary)',
                              },
                              '&:hover fieldset': {
                                borderColor: errors?.otp
                                  ? 'var(--forbrand-error)'
                                  : 'var(--forbrand-primary)',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: errors?.otp
                                  ? 'var(--forbrand-error)'
                                  : 'var(--forbrand-color)',
                              },
                              '&.Mui-disabled': {
                                backgroundColor: 'var(--bg-disabled)',
                              },
                            },
                            '& .MuiInputBase-input::placeholder': {
                              color: '#999',
                              opacity: 1,
                            },
                            '.MuiOutlinedInput-input': {
                              '&::-webkit-outer-spin-button': {
                                WebkitAppearance: 'none',
                                margin: 0,
                              },
                              '&::-webkit-inner-spin-button': {
                                WebkitAppearance: 'none',
                                margin: 0,
                              },
                            },
                          }}
                          slotProps={{
                            formHelperText: {
                              sx: { margin: 0 },
                            },
                            htmlInput: { maxLength: 6 },
                          }}
                        />

                        <Typography variant="span" className="text-green-700">
                          OTP has been sent to {otpSentDetails?.maskedEmail}
                        </Typography>
                        <Button
                          variant="contained"
                          onClick={handleNext}
                          disabled={loading}
                          sx={{
                            backgroundColor: 'var(--forbrand-bg-variant)',
                            color: 'var(--forbrand-brand-varient)',
                            width: '50%',
                          }}
                        >
                          {loading ? 'Verifying...' : 'Submit'}
                        </Button>
                      </Box>
                    </>
                  )}
                  {errors.otp && !otpSent && (
                    <Alert severity="error" sx={{ mt: 2, width: '100%' }}>
                      {errors.otp}
                    </Alert>
                  )}
                </Box>
              )}
            </FormControl>
          </Box>
        )

      case 2:
        return (
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography
              variant="h5"
              sx={{ mb: 3, fontWeight: 600, fontSize: '14px' }}
            >
              Follow below steps to enable multi factor authentication
            </Typography>
            <Typography
              variant="body1"
              sx={{ mb: 4, color: '#333', lineHeight: 1.6, fontSize: '14px' }}
            >
              As a next step, you would have to enter OTP along with username
              and password to login the application
            </Typography>

            <Typography
              variant="h6"
              sx={{ mb: 3, fontWeight: 600, fontSize: '14px' }}
            >
              Download any of the OTP Provider application from
              Playstore/Appstore given below
            </Typography>

            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h6"
                sx={{
                  mb: 2,
                  color: 'var(--forbrand-primary)',
                  fontSize: '14px',
                }}
              >
                Google Authenticator
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: 2,
                  mb: 3,
                }}
              >
                <Box
                  component="img"
                  src="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTMwIiBoZWlnaHQ9IjQwIiB2aWV3Qm94PSIwIDAgMTMwIDQwIiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPgo8cmVjdCB3aWR0aD0iMTMwIiBoZWlnaHQ9IjQwIiByeD0iNiIgZmlsbD0iIzAwMCIvPgo8dGV4dCB4PSI2NSIgeT0iMjQiIGZpbGw9IndoaXRlIiBmb250LXNpemU9IjEyIiBmb250LWZhbWlseT0iQXJpYWwsIHNhbnMtc2VyaWYiIHRleHQtYW5jaG9yPSJtaWRkbGUiPkdldCBpdCBvbiBHb29nbGUgUGxheTo8L3RleHQ+Cjwvc3ZnPgo="
                  alt="Google Play"
                  sx={{ height: 40, cursor: 'pointer' }}
                />
                <Box
                  component="img"
                  src="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTMwIiBoZWlnaHQ9IjQwIiB2aWV3Qm94PSIwIDAgMTMwIDQwIiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPgo8cmVjdCB3aWR0aD0iMTMwIiBoZWlnaHQ9IjQwIiByeD0iNiIgZmlsbD0iIzAwMCIvPgo8dGV4dCB4PSI2NSIgeT0iMjQiIGZpbGw9IndoaXRlIiBmb250LXNpemU9IjEyIiBmb250LWZhbWlseT0iQXJpYWwsIHNhbnMtc2VyaWYiIHRleHQtYW5jaG9yPSJtaWRkbGUiPkRvd25sb2FkIG9uIEFwcCBTdG9yZTo8L3RleHQ+Cjwvc3ZnPgo="
                  alt="App Store"
                  sx={{ height: 40, cursor: 'pointer' }}
                />
              </Box>

              <Typography
                variant="h6"
                sx={{
                  mb: 2,
                  color: 'var(--forbrand-primary)',
                  fontSize: '14px',
                }}
              >
                Free OTP
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                <Box
                  component="img"
                  src="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTMwIiBoZWlnaHQ9IjQwIiB2aWV3Qm94PSIwIDAgMTMwIDQwIiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPgo8cmVjdCB3aWR0aD0iMTMwIiBoZWlnaHQ9IjQwIiByeD0iNiIgZmlsbD0iIzAwMCIvPgo8dGV4dCB4PSI2NSIgeT0iMjQiIGZpbGw9IndoaXRlIiBmb250LXNpemU9IjEyIiBmb250LWZhbWlseT0iQXJpYWwsIHNhbnMtc2VyaWYiIHRleHQtYW5jaG9yPSJtaWRkbGUiPkdldCBpdCBvbiBHb29nbGUgUGxheTo8L3RleHQ+Cjwvc3ZnPgo="
                  alt="Google Play"
                  sx={{ height: 40, cursor: 'pointer' }}
                />
                <Box
                  component="img"
                  src="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTMwIiBoZWlnaHQ9IjQwIiB2aWV3Qm94PSIwIDAgMTMwIDQwIiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPgo8cmVjdCB3aWR0aD0iMTMwIiBoZWlnaHQ9IjQwIiByeD0iNiIgZmlsbD0iIzAwMCIvPgo8dGV4dCB4PSI2NSIgeT0iMjQiIGZpbGw9IndoaXRlIiBmb250LXNpemU9IjEyIiBmb250LWZhbWlseT0iQXJpYWwsIHNhbnMtc2VyaWYiIHRleHQtYW5jaG9yPSJtaWRkbGUiPkRvd25sb2FkIG9uIEFwcCBTdG9yZTo8L3RleHQ+Cjwvc3ZnPgo="
                  alt="App Store"
                  sx={{ height: 40, cursor: 'pointer' }}
                />
              </Box>
            </Box>
          </Box>
        )

      case 3:
        return (
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography
              variant="h5"
              sx={{ mb: 3, fontWeight: 600, fontSize: '14px' }}
            >
              Upon Installing the app on your mobile phone device, follow the
              instructions
            </Typography>

            <Typography
              variant="body1"
              sx={{ mb: 4, fontWeight: 500, fontSize: '14px' }}
            >
              Scan the QR Code below
            </Typography>

            {/* Success Message */}
            {successMessage && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {successMessage}
              </Alert>
            )}

            {/* QR Code */}
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center' }}>
              <Box
                sx={{
                  width: 200,
                  height: 200,
                  border: '2px solid #000',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#fff',
                  borderRadius: 1,
                }}
              >
                <svg width="180" height="180" viewBox="0 0 180 180">
                  <defs>
                    <pattern
                      id="qrPattern"
                      patternUnits="userSpaceOnUse"
                      width="6"
                      height="6"
                    >
                      <rect width="6" height="6" fill="white" />
                      <rect width="3" height="3" fill="black" />
                    </pattern>
                  </defs>
                  {/* QR Code simulation - you can replace with actual QR code */}
                  <rect width="180" height="180" fill="url(#qrPattern)" />
                  <rect x="10" y="10" width="30" height="30" fill="black" />
                  <rect x="140" y="10" width="30" height="30" fill="black" />
                  <rect x="10" y="140" width="30" height="30" fill="black" />
                  <rect x="15" y="15" width="20" height="20" fill="white" />
                  <rect x="145" y="15" width="20" height="20" fill="white" />
                  <rect x="15" y="145" width="20" height="20" fill="white" />
                </svg>
              </Box>
            </Box>

            <Box className="flex flex-col justify-center items-center gap-5">
              <TextField
                fullWidth
                placeholder="Please Enter Authentication Code..."
                value={authenticatorCode}
                onChange={e => {
                  setAuthenticatorCode(e.target.value)
                  if (errors.authenticatorCode) {
                    setErrors(prev => ({ ...prev, authenticatorCode: '' }))
                  }
                }}
                variant="outlined"
                error={!!errors.authenticatorCode}
                helperText={errors.authenticatorCode}
                sx={{
                  width: '50%',
                  '.MuiOutlinedInput-input': {
                    padding: '12px 20px',
                  },
                }}
              />
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={loading}
                sx={{
                  bgcolor: '#000',
                  color: 'white',
                  '&:hover': { bgcolor: '#333' },
                  borderRadius: 1,
                  textTransform: 'none',
                  px: 4,
                  py: 1,
                }}
              >
                {loading ? 'Verifying...' : 'Submit'}
              </Button>
            </Box>
          </Box>
        )

      default:
        return null
    }
  }

  const getNextButtonText = () => {
    switch (activeStep) {
      case 0:
        return 'Next Step'
      case 1:
        return mfaMethod === 'mail' && !otpSent ? 'Continue' : 'Next Step'
      case 2:
        return 'Continue'
      default:
        return 'Continue'
    }
  }

  const isNextButtonDisabled = () => {
    if (loading) return true

    switch (activeStep) {
      case 0:
        return !passwords.old || !passwords.new || !passwords.confirm
      case 1:
        if (mfaMethod === 'mail' && otpSent) {
          return !otp
        }
        return false
      case 3:
        if (mfaMethod === 'google') {
          return !authenticatorCode
        }
        return false
      default:
        return false
    }
  }

  return (
    <Box>
      {/* Custom Stepper */}
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
          }}
        >
          {steps.map((label, index) => (
            <React.Fragment key={label}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                {/* Step Circle */}
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor:
                      activeStep === index
                        ? 'var(--forbrand-bg-variant)'
                        : '#e0e0e0',
                    color:
                      activeStep === index ? 'var(--forbrand-primary)' : '#999',
                    fontSize: '16px',
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  {index + 1}
                </Box>
                {/* Step Label */}
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '12px',
                    fontWeight: activeStep === index ? 600 : 400,
                    color:
                      activeStep === index
                        ? 'var(--forbrand-primary)'
                        : 'var(--forbrand-primary)',
                    textAlign: 'center',
                    lineHeight: 1.2,
                    pb: 1,
                    borderBottom:
                      activeStep === index &&
                      '3px solid var(--forbrand-bg-variant)',
                  }}
                >
                  {label}
                </Typography>
              </Box>
              {/* Dotted Line */}
              {index < steps.length - 1 && (
                <Box
                  sx={{
                    flex: 1,
                    height: 2,
                    mx: 2,
                    mb: 3,
                    background:
                      'repeating-linear-gradient(to right, #ccc 0, #ccc 4px, transparent 4px, transparent 8px)',
                  }}
                />
              )}
            </React.Fragment>
          ))}
        </Box>
      </Box>

      {/* Step Content */}
      {renderStepContent(activeStep)}

      {/* Navigation Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 4 }}>
        {activeStep > 0 && !(activeStep === 1 && !userDetails?.mfaEnable) && (
          <Button
            onClick={handleBack}
            variant="text"
            sx={{ color: 'var(--forbrand-primary)' }}
          >
            Back
          </Button>
        )}

        {activeStep < steps.length - 1 &&
          // Don't show next button for step 1 if OTP is sent (handled by submit button in OTP section)
          !(activeStep === 1 && mfaMethod === 'mail' && otpSent) && (
            <Button
              onClick={handleNext}
              variant="contained"
              disabled={isNextButtonDisabled()}
              sx={{
                backgroundColor: 'var(--forbrand-bg-variant)',
                color: 'var(--forbrand-brand-varient)',
                '&:disabled': { bgcolor: '#ccc' },
                borderRadius: 1,
                textTransform: 'none',
                px: 4,
              }}
            >
              {loading ? 'Processing...' : getNextButtonText()}
            </Button>
          )}
      </Box>
    </Box>
  )
}
